import * as React from "react";
import { HomeIcons, type IconName } from "assets/icons";

type AppIconProps = {
  /** Lookup by registry key, e.g. "building" | "salesOrder" */
  name?: IconName;
  /** Or pass an imported icon URL directly */
  src?: string;
  /**
   * Sets both width and height in pixels.
   * Example: size={18} → 18x18, size={24} → 24x24
   */
  size?: number;
  alt?: string;
  className?: string;
} & Omit<React.ImgHTMLAttributes<HTMLImageElement>, "src" | "alt" | "width" | "height">;

/**
 * Renders a custom icon from the central sharedIcons registry.
 *
 *   <AppIcon name="building" size={24} alt="Brokerages" />
 *   <AppIcon name="salesOrder" size={22} />
 */
export function AppIcon({
  name,
  src,
  size,
  alt = "",
  className,
  style,
  ...props
}: AppIconProps) {
  const iconSrc = src ?? (name ? HomeIcons[name] : undefined);

  if (!iconSrc) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[AppIcon] Missing icon. Pass `name` or `src`.");
    }
    return null;
  }

  const sizeStyle =
    size != null ? { width: size, height: size, ...style } : style;

  return (
    <img
      src={iconSrc}
      alt={alt}
      width={size}
      height={size}
      className={["inline-block", "object-contain", "shrink-0", className]
        .filter(Boolean)
        .join(" ")}
      style={sizeStyle}
      draggable={false}
      {...props}
    />
  );
}

export type { IconName };
